import React from 'react';
import {
  AlertTriangle,
  Clock,
  CalendarX,
  MessageCircle,
  IndianRupee,
  AlertCircle
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { RiskIndicator } from '@/types/dashboard';

interface RiskBadgeProps {
  type: RiskIndicator['type'];
  severity: RiskIndicator['severity'];
  showTooltip?: boolean;
}

const RISK_CONFIG = {
  NO_ACTIVITY_7_DAYS: {
    label: 'No Activity 7d',
    icon: Clock,
    description: 'No activity recorded in the last 7 days',
  },
  OVERDUE_FOLLOWUP: {
    label: 'Overdue Follow-up',
    icon: CalendarX,
    description: 'Follow-up is overdue',
  },
  LONG_IN_STAGE: {
    label: 'Long in Stage',
    icon: AlertCircle,
    description: 'Lead has been in current stage for extended period',
  },
  NO_CONTACT_14_DAYS: {
    label: 'No Contact 14d',
    icon: MessageCircle,
    description: 'No contact made in the last 14 days',
  },
  MISSING_DEAL_VALUE: {
    label: 'Missing Deal Value',
    icon: IndianRupee,
    description: 'Deal value not specified',
  },
  OVERDUE_EXPECTED_CLOSE: {
    label: 'Overdue Close',
    icon: AlertTriangle,
    description: 'Expected close date has passed',
  },
};

export const RiskBadge: React.FC<RiskBadgeProps> = ({ type, severity, showTooltip = true }) => {
  const config = RISK_CONFIG[type];
  const Icon = config.icon;

  const severityColors = {
    HIGH: 'bg-red-100 text-red-800 border-red-300',
    MEDIUM: 'bg-yellow-100 text-yellow-800 border-yellow-300',
  };

  const iconColors = {
    HIGH: 'text-red-600',
    MEDIUM: 'text-yellow-600',
  };

  return (
    <div className="relative group">
      <Badge
        variant="outline"
        className={`${severityColors[severity]} text-xs font-medium flex items-center gap-1 px-2 py-1`}
      >
        <Icon className={`h-3 w-3 ${iconColors[severity]}`} />
        <span>{config.label}</span>
      </Badge>

      {showTooltip && (
        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-md opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
          {config.description}
          <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1 border-4 border-transparent border-t-gray-900"></div>
        </div>
      )}
    </div>
  );
};
